/* eslint-disable no-loop-func */
import { createReducer } from '@reduxjs/toolkit';

import initialState from './initial-state';
import actionTypes from '../actions/action-types';
import { merge, toCSV, toMapCSV, getLetterFromIndex, getLetterIndex } from 'editor/core/highcharts-editor';
import templates from 'editor/core/highed.template.plugins';
import { cloneDeep, isArray, isObject } from 'lodash';
import { shuffleArray } from 'editor/core-ui/datatable/highed.datatable.utils';

const getFieldsUsed = (seriesAssigns) => {
  let obj = {};
  seriesAssigns.forEach((option) => {
    const keys = Object.keys(option);
    keys.forEach((key) => {
      if (option[key].rawValue) {
        obj[option[key].rawValue[0]] = true;
      }
    });
  });
  let keys = Object.keys(obj);
  return keys.map((k) => parseInt(k, 10)).sort((a, b) => a - b);
};

const saveDataAndConfigAsCSV = (state, isMap) => {
  const { seriesAssigns } = state;
  const sections = getFieldsUsed(seriesAssigns);
  let newArray = [];
  if (state.templateMeta?.[0]?.templateParseData && templates[state.templateMeta[0].templateParseData].parseData) {
    const parseData = state.templateMeta?.[0]?.templateParseData;
    newArray = templates[parseData].parseData(cloneDeep(state.dataOptions), null, state.customizedOptions);
    // eslint-disable-next-line no-use-before-define
    setSeriesMapping(state, newArray);
  } else {
    newArray = state.dataOptions.map((row) => {
      let arrRow = [];
      row.forEach((col, colIndex) => {
        const index = sections.indexOf(colIndex);
        if (index > -1) {
          arrRow[index] = col;
        }
      });
      return arrRow;
    });
  }

  const csv = isMap ? toMapCSV(newArray, ';') : toCSV(newArray);
  if (state.customizedOptions.data) state.customizedOptions.data.csv = csv;
  if (state.aggregatedOptions.data) state.aggregatedOptions.data.csv = csv;
};

const clearSeriesMapping = (state) => {
  if (state.customizedOptions.data?.seriesMapping) {
    delete state.customizedOptions.data.seriesMapping;
  }
  if (state.aggregatedOptions.data?.seriesMapping) {
    delete state.aggregatedOptions.data.seriesMapping;
  }
};

const removeUnnecesaryRows = (dataOptions) => {
  let maxRowLen = 0;

  dataOptions.forEach((row, rowIndex) => {
    const rowEmpty = row.every((cell) => cell === null || cell === undefined || cell === '');
    if (!rowEmpty) maxRowLen = Math.max(rowIndex, maxRowLen);
  });

  dataOptions.splice(maxRowLen + 1, dataOptions.length - maxRowLen - 1);
};

const setSeriesMapping = (state, dataOverride) => {
  let { customizedOptions, seriesAssigns, aggregatedOptions } = state;

  let tempOption = [],
    dataTableFields = getFieldsUsed(seriesAssigns),
    hasLabels = false,
    hasExtraColumn = [],
    extraColumnBuffer = 0;

  // Some templates need to have some custom parsing done to the seriesmapping. ex: Packed bubble
  const template = state.templateMeta;
  if (template?.[0]?.templateParseData && templates[template[0].templateParseData].parseAssigns) {
    const parseAssigns = templates[template[0].templateParseData].parseAssigns;
    const seriesMapping = parseAssigns(dataOverride, seriesAssigns, null, state.customizedOptions);
    // Really unoptimized having to call this 'parseAssigns' twice, once we have redone the editor,
    // replace the above function to change both customized and aggregated options at once
    parseAssigns(dataOverride, seriesAssigns, null, state.aggregatedOptions);
    state.customizedOptions.data.seriesMapping = seriesMapping;
    state.aggregatedOptions.data.seriesMapping = seriesMapping;
    return;
  }

  let series = seriesAssigns.length;

  for (let i = 0; i < series; i++) {
    let serieOption = {};
    Object.keys(seriesAssigns[i]).forEach((key) => {
      const option = seriesAssigns[i][key];
      if (option.value !== '') {
        if (option.isData) {
          if (dataTableFields.indexOf(option.rawValue[0]) > -1) {
            let linkedTo = isArray(option.linkedTo) ? option.linkedTo : [option.linkedTo];
            linkedTo.forEach((linked) => {
              serieOption[linked] = dataTableFields.indexOf(option.rawValue[0]);
            });
          }
        } else if (isObject(option)) {
          if (option.linkedTo === 'label') hasLabels = true;
          if (dataTableFields.indexOf(option.rawValue[0]) > -1) {
            let linkedTo = isArray(option.linkedTo) ? option.linkedTo : [option.linkedTo];
            linkedTo.forEach((linked) => {
              serieOption[linked] = dataTableFields.indexOf(option.rawValue[0]);
            });
          }
        }

        if (option.addExtraColumn) {
          hasExtraColumn.push(option.linkedTo);
          extraColumnBuffer = option.addExtraColumn;
        }
      }
    });
    tempOption.push(serieOption);
  }

  if (tempOption.length > 0) {
    if (hasLabels) {
      const dataLabelOptions = {
        dataLabels: {
          enabled: true,
          format: '{point.label}'
        }
      };

      if (customizedOptions.plotOptions) {
        const seriesPlotOptions = customizedOptions.plotOptions.series;
        const aggregatedSeriesPlotOptions = aggregatedOptions.plotOptions.series;
        merge(seriesPlotOptions, dataLabelOptions);
        merge(aggregatedSeriesPlotOptions, dataLabelOptions);
      } else {
        const option = {
          plotOptions: {
            series: dataLabelOptions
          }
        };
        merge(customizedOptions, option);
        merge(aggregatedOptions, option);
      }
    }

    if (hasExtraColumn.length) {
      tempOption.forEach((opt) => {
        const keys = Object.keys(opt);
        keys.forEach((key) => {
          if (hasExtraColumn.indexOf(key) === -1) {
            opt[key] += extraColumnBuffer;
          }
        });
      });
    }

    if (customizedOptions.data) {
      state.customizedOptions.data.seriesMapping = tempOption;
      state.aggregatedOptions.data.seriesMapping = tempOption;
    }
  }
};

const updateCell = (dataOptions, row, column, newValue) => {
  const rowToUpdate = dataOptions[row];
  const emptyRow = [...Array(dataOptions[0].length)];
  const newArr = rowToUpdate || [...Array(dataOptions[0].length)];
  const dataOptionsLength = dataOptions.length;

  newArr[column] = newValue;
  dataOptions[row] = newArr;
  // this operation allows to add values in any place inside datagrid and avoid throwing HandsOnTable error

  if (row > dataOptionsLength) {
    for (let i = 0; i < dataOptions.length; i++) {
      if (!dataOptions[i]) dataOptions[i] = emptyRow;
    }
  }
};

const getAssignDataFields = (options) => {
  let all = [];

  options.forEach((option) => {
    let arr = {};
    Object.keys(option).forEach((key) => {
      if (option[key].value === '' || option[key].value === null) return;
      arr[key] = option[key].value;
    });
    all.push(merge({}, arr));
  });

  return all;
};

const deleteColumnFromOptions = (customizedOptions, aggregatedOptions, key, index) => {
  customizedOptions[key].splice(index, 1);
  if (aggregatedOptions[key]) aggregatedOptions[key].splice(index, 1);
};

const handleDeletedColumn = (state, data) => {
  let { index, amount, key: optionKey, skipRemap } = data;
  let { seriesAssigns, customizedOptions, aggregatedOptions } = state;
  for (let i = 0; i < amount; i++) {
    const letter = getLetterFromIndex(index).toUpperCase();
    getAssignDataFields(seriesAssigns).forEach((serie, seriesIndex) => {
      Object.keys(serie).forEach((key) => {
        if (serie[key] === letter) {
          if (seriesAssigns[seriesIndex] && seriesAssigns[seriesIndex][key]) {
            if (seriesAssigns[seriesIndex][key].isData) {
              // Is a data column, delete the series
              seriesAssigns.splice(seriesIndex, 1);
              deleteColumnFromOptions(customizedOptions, aggregatedOptions, optionKey, seriesIndex);
            } else if (!seriesAssigns[seriesIndex][key].isLabel) {
              // Isn't data, just remove the mapping instead
              seriesAssigns[seriesIndex][key].value = '';
              seriesAssigns[seriesIndex][key].rawValue = null;
              seriesAssigns[seriesIndex][key].previousValue = null;
            }
          }
        }
      });
    });

    if (!skipRemap) {
      getAssignDataFields(seriesAssigns).forEach((serie, seriesIndex) => {
        Object.keys(serie).forEach((key) => {
          // Go through each assigns and remove 1 from index if they are bigger than `which`
          if (getLetterIndex(serie[key]) > index) {
            seriesAssigns[seriesIndex][key].value = getLetterFromIndex(getLetterIndex(serie[key]) - 1).toUpperCase();
            seriesAssigns[seriesIndex][key].rawValue = [getLetterIndex(serie[key]) - 1];
          }
        });
      });
    }
  }
};

function isNumeric(n) {
  return !isNaN(parseFloat(n)) && isFinite(n);
}

/** Sort rows
 * @memberof DataTable
 * @param column {number} - the column to sort on
 * @param direction {string} - the direction: `asc` or `desc`
 */
export function sort(rows, column, direction) {
  // eslint-disable-next-line array-callback-return
  rows.sort((a, b) => {
    let ad = a[column],
      bd = b[column];

    if (isNumeric(ad) && isNumeric(bd)) {
      ad = parseFloat(ad);
      bd = parseFloat(bd);

      if (direction === 'asc') {
        return ad - bd;
      }

      // eslint-disable-next-line no-nested-ternary
      return bd < ad ? -1 : bd > ad ? 1 : 0;
    } else if (ad === null) {
      return 1;
    } else if (bd === null) {
      return -1;
    }

    if (direction === 'asc') {
      if (!ad) return bd;
      return ad.localeCompare(bd);
    }

    if (bd) {
      if (!ad) return bd;
      return bd.toString().localeCompare(ad);
    } else if (ad) {
      return ad.localeCompare(bd);
    }
  });
}

const modifyLaterAssignRawOptions = (state, data) => {
  let { index } = data;
  let { seriesAssigns } = state;
  getAssignDataFields(seriesAssigns).forEach((serie, seriesIndex) => {
    Object.keys(serie).forEach((key) => {
      // Go through each assigns and remove 1 from index if they are bigger than `which`
      if (getLetterIndex(serie[key]) >= index) {
        seriesAssigns[seriesIndex][key].value = getLetterFromIndex(getLetterIndex(serie[key]) + 1).toUpperCase();
        seriesAssigns[seriesIndex][key].rawValue = [getLetterIndex(serie[key]) + 1];
      }
    });
  });
};

const projectConfigReducer = createReducer(initialState.projectConfig, (builder) => {
  builder
    .addCase(actionTypes.projectConfig.set, (state, action) => {
      Object.assign(state, action.data);
    })
    .addCase(actionTypes.projectConfig.updateDataAndConfig, (state, action) => {
      const { row, column, newValue, isMap, skipSeriesMapping } = action.data;
      const { dataOptions } = state;

      updateCell(dataOptions, row, column, newValue);

      dataOptions.forEach((row) => {
        if (column + 1 > row.length) row.push(...Array(column + 1 - row.length));
      });

      if (!skipSeriesMapping) {
        saveDataAndConfigAsCSV(state, isMap);
      }
      state.changeMade = true;
    })
    // Used when editing either one or multiple cells
    .addCase(actionTypes.projectConfig.updateDataAndConfigBulk, (state, action) => {
      let { changes, isMap, skipEmit } = action.data;
      const { dataOptions } = state;
      const allNulls = changes.every((change) => change[3] === null || change[3] === '');

      for (const [row, column, , newValue] of changes) {
        updateCell(dataOptions, row, column, newValue);
      }

      const lastChange = changes[changes.length - 1];
      dataOptions.forEach((row) => {
        if (lastChange[1] + 1 > row.length) row.push(...Array(lastChange[1] + 1 - row.length));
      });

      if (allNulls && !isMap) {
        // User has deleted one or more cells
        // Check that the dataOptions array doesnt end with empty rows
        // If it does, remove them

        removeUnnecesaryRows(dataOptions);

        let columnsDeleted = {};
        let deletedColumn = false;
        changes.forEach((change) => {
          columnsDeleted[change[1]] = (columnsDeleted[change[1]] ?? 0) + 1;
        });
        const keys = Object.keys(columnsDeleted).sort().reverse();
        keys.forEach((columnIndex) => {
          if (columnsDeleted[columnIndex] >= dataOptions.length) {
            // Deleted the whole column, see if there are any mapped values.
            // If so, remove them
            deletedColumn = true;
            if (state.seriesAssigns.length === 1) return;
            handleDeletedColumn(state, {
              index: columnIndex,
              amount: 1,
              skipRemap: true,
              key: skipEmit ? 'columns' : 'series'
            });
          }
        });

        if (deletedColumn) {
          const templateParsing = state.templateMeta?.[0]?.templateParseData;
          const hasSpecialParsing = templateParsing && templates[templateParsing].parseData;
          clearSeriesMapping(state);
          if (!hasSpecialParsing) setSeriesMapping(state);
        }
      }

      if (!skipEmit) saveDataAndConfigAsCSV(state, isMap);
      state.changeMade = true;
    })
    .addCase(actionTypes.projectConfig.clearDataGrid, (state, action) => {
      const { skipEmit, isMap } = action.data;
      const { dataOptions } = state;

      dataOptions.forEach((row) => {
        for (let i = 0; i < row.length; i++) {
          row[i] = '';
        }
      });

      if (!skipEmit) {
        saveDataAndConfigAsCSV(state, isMap);
      }
      state.changeMade = true;
    })
    .addCase(actionTypes.projectConfig.movedColumns, (state, action) => {
      const { movedCells, finalIndex, isMap, skipEmit } = action.data;
      const { dataOptions } = state;

      dataOptions.forEach((row) => {
        shuffleArray(row, movedCells[0], movedCells.length, finalIndex);
      });

      if (!skipEmit) saveDataAndConfigAsCSV(state, isMap);
      state.changeMade = true;
    })
    .addCase(actionTypes.projectConfig.movedRows, (state, action) => {
      const { movedCells, finalIndex, isMap } = action.data;
      const { dataOptions } = state;

      shuffleArray(dataOptions, movedCells[0], movedCells.length, finalIndex);
      saveDataAndConfigAsCSV(state, isMap);
      state.changeMade = true;
    })
    .addCase(actionTypes.projectConfig.addDataSeries, (state, action) => {
      const { index, amount, source, skipEmit } = action.data;
      const { dataOptions } = state;
      const emptyRow = [...Array(state.dataOptions[0].length)];

      switch (source) {
        case 'ContextMenu.rowAbove':
        case 'ContextMenu.rowBelow':
          dataOptions.splice(index, amount - 1, emptyRow);
          break;
        case 'ContextMenu.columnLeft':
        case 'ContextMenu.columnRight':
          dataOptions.map((row) => row.splice(index, amount - 1, undefined));
          break;
        default:
          break;
      }

      if (source === 'ContextMenu.columnLeft' || source === 'ContextMenu.columnRight') {
        // For all seriesAssigns after this index, we want to go through and +1 to the data fields.
        modifyLaterAssignRawOptions(state, { index });
      }

      if (!skipEmit) saveDataAndConfigAsCSV(state);
      state.changeMade = true;
    })
    .addCase(actionTypes.projectConfig.clearColumn, (state, action) => {
      const { index, amount, skipEmit } = action.data;
      let { dataOptions } = state;

      handleDeletedColumn(state, { ...action.data, skipRemap: true });

      if (!skipEmit) clearSeriesMapping(state);
      dataOptions.map((row) => row.splice(index, amount, ...new Array(amount).fill(null)));

      if (!skipEmit) {
        setSeriesMapping(state);
        saveDataAndConfigAsCSV(state);
      }
      state.changeMade = true;
      return state;
    })
    .addCase(actionTypes.projectConfig.deleteSeries, (state, action) => {
      const { index, amount, source, skipEmit } = action.data;
      let { dataOptions, seriesAssigns } = state;

      if (!skipEmit) clearSeriesMapping(state, seriesAssigns);

      switch (source) {
        case 'ContextMenu.removeRow':
          dataOptions.splice(index, amount);
          break;
        case 'ContextMenu.removeColumn':
          dataOptions.map((row) => row.splice(index, amount));
          break;
        default:
          break;
      }

      removeUnnecesaryRows(dataOptions);

      if (!skipEmit) {
        setSeriesMapping(state);
        saveDataAndConfigAsCSV(state);
      }
      state.changeMade = true;
      return state;
    })
    .addCase(actionTypes.projectConfig.sortData, (state, action) => {
      const { column, sortOrder, isMap } = action.data;
      const { dataOptions } = state;

      const headers = dataOptions.shift();
      sort(dataOptions, column, sortOrder);
      dataOptions.unshift(headers);
      saveDataAndConfigAsCSV(state, isMap);
      state.changeMade = true;
    })
    .addCase(actionTypes.projectConfig.handleDeletedColumn, (state, action) => {
      const { seriesAssigns } = state;

      if (seriesAssigns.length === 1) return state;

      handleDeletedColumn(state, action.data);
      state.changeMade = true;
    })
    .addCase(actionTypes.projectConfig.deleteColumnFromOptions, (state, action) => {
      const { customizedOptions, aggregatedOptions } = state;
      const { key, index } = action.data;

      deleteColumnFromOptions(customizedOptions, aggregatedOptions, key, index);
      state.changeMade = true;
    })
    .addCase(actionTypes.projectConfig.updateSeriesMapping, (state, action) => {
      const { newAssigns, isMap } = action.data;
      if (newAssigns) state.seriesAssigns = newAssigns;
      setSeriesMapping(state);
      saveDataAndConfigAsCSV(state, isMap);
      state.changeMade = true;
    })
    .addCase(actionTypes.projectConfig.changeProjectName, (state, action) => {
      state.projectName = action.data.projectName;
    })
    .addCase(actionTypes.projectConfig.registerChangeMade, (state) => {
      state.changeMade = true;
    })
    .addCase(actionTypes.projectConfig.unsetChangeMade, (state) => {
      state.changeMade = false;
    })
    .addCase(actionTypes.projectConfig.toggleInspired, (state, action) => {
      state.toggleInspired = action.data;
    })
    .addCase(actionTypes.projectConfig.toggleUnsavedChangesModal, (state, action) => {
      state.showUnsavedChangesModal = action.data;
    })
    .addCase(actionTypes.projectConfig.toggleUnpublishModal, (state, action) => {
      state.showUnpublishModal = action.data;
    })
    .addCase(actionTypes.projectConfig.reset, () => {
      return initialState.projectConfig;
    })
    .addCase(actionTypes.projectConfig.resetTheme, (state) => {
      state.themeOptions = {
        options: {}
      };
    });
});

export default projectConfigReducer;
